import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { MSpinner, MField } from "components/Shared";
import { Button, TextField } from "react-md";
import StarRatings from "react-star-ratings";
import { isSatisfactionSurveyAvailable, setSatisfactionSurvey } from "requests/satisfactionSurvey/onboarding";
import responseHandler from "responseHandler";
import { OnboardingSatisfactionSurveyUpdateRequest } from "interfaces/Mueller.Business.Models";
import { DATA_TYPE_DROPDOWN2, DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";

interface Props {
  match: { params: { uId: string } },
  makeToast: (arg: string) => void,
}

let STATUS = {
  NONE: "NONE",
  FETCHING: "FETCHING",
  READY: "READY",
  RATED: "RATED",
  EXPIRED: "EXPIRED",
  ERROR: "ERROR",
};

const OnboardingSatisfactionSurvey = (props: Props) => {
  const mounted = useRef(true);
  const { match: { params }, makeToast } = props;  
  const [status, setStatus] = useState(STATUS.NONE);

  const COMMENTS_MAXLENGTH = 3500;

  const starRatedColorConst ="#E03D4C";
  const starHoverColorConst ="#CA2030";

  const [surveyResponse, setSurveyResponse] = useState<OnboardingSatisfactionSurveyUpdateRequest>({
    uid: params.uId,
    onboardingExperienceScore: null,
    didYouCompleteViitaCourse: null,
    wasViitaCourseHelpful: true,
    whatCouldBeImprovedAboutViitaCourse: "",
    recruiterOnboardingScore: null,
    wasRecruiterClearDuringScreeningCall: null,
    whatWasUnclearDuringScreeningCall: "",
    didRecruiterProvideHelpfulSupportDuringScreeningCall: null,
    whereDidSupportBreakDownDuringScreeningCall: "",
    didRecuiterResolveTechnicalIssuesPromptly: null,
    whatSupportWasNotProvided: "",
    didOnboardingPaperworkMakeFormCompletionEasy: null,
    whatWasConfusingWithOnboardingPaperwork: "",
    wereYouAbleToLogIntoSystemsWithoutIssue: null,
    whichSystemsCausedTrouble: "",
    managerOnboardingSupportScore: null,
    didYouMeetWithManagerWithinTwoDaysOfHire: null,
    whenDidYouFirstMeetWithManager: "",
    didOrientationClarifyExpectationsForFirstDay: null,
    whatCouldHaveImprovedOrientation: "",
    didTrainingMaterialsHelpPrepareForFirstInspection: null,
    whatCouldHaveBeenImprovedWithTrainingMaterials: "",
    wereYouProvidedNumberForFieldSupportHotline: null,
    didYouFeelSupportedByYourManagerDuringFirstTwoWeeks: null,
    whatSupportDidYouNotReceive: "",
    howLongAfterInitialMeetingDidYouCompleteAnInspection: 1,
    whatCausedTheDelay: "",
    didYouFeelConfidentBeforeCompletingYourFirstInspection: null,
    whatMadeYouFeelUnprepared: "",
    didYouReceiveEnoughInspectionsInFirstTwoWeeks: null,
    didYouLetYourManagerKnowYouNeededMoreWork: "",
    doYouUnderstandYourJobResponsibilitiesToday: null,
    whatResponsibilitiesCouldBeClarified: "",
    doYouUnderstandHowYourPerformanceIsMeasured: null,
    whatIsUnclearAboutPerformanceExpectations: "",
    additionaFeedbackForOnboardingExperience: "",
  });

  useEffect(() => {
    if(status === STATUS.NONE)
      fetchStatus();

    return () => { mounted.current = false; };
  }, []);

  const fetchStatus = () => {      
    isSatisfactionSurveyAvailable(
      params.uId, 
      (data) => { data && data.canCompleteSatisfactionSurvey ? setStatus(STATUS.READY) : setStatus(STATUS.EXPIRED); },
      () => { setStatus(STATUS.ERROR); },
      mounted
    );    
    setStatus(STATUS.FETCHING);
  };

  const updateSurveyResponse = (key : string, val : any) => {
    setSurveyResponse((prevSurveyResponse) => ({
      ...prevSurveyResponse,
      [key]: val
    }));
  }
  
  const submitFeedback = () => {  
    let passesValidation = null;
    let responseToSubmit = {...surveyResponse};    
    responseToSubmit.uid = params.uId;
    Object.keys(responseToSubmit).forEach((key) => {
      const element = document.getElementById(key);
      if (responseToSubmit[key] === null) {
        passesValidation = key;
        if (element) {
          element.classList.add("error-highlight"); // Add red text class
        }
      } else {
        if (element) {
          element.classList.remove("error-highlight"); // Remove red text class
        }
      }
    });

    if(passesValidation !== null){  
      const element = document.getElementById(passesValidation);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }      
      makeToast("Please fill out each question before submitting.");
      return;
    }
    
    setSatisfactionSurvey(params.uId, responseToSubmit, () => { 
      setStatus(STATUS.RATED); 
    }, () => setStatus(STATUS.READY), mounted);    
    setStatus(STATUS.FETCHING);
  };

  return (
    <div className="satisfaction-survey-root">
      {(status === STATUS.NONE || status === STATUS.FETCHING) && <MSpinner />}
      {status === STATUS.READY &&
        <>        
          <h3>Your feedback is important to us!</h3>
          <div className="rating-tip">(1 - Very Poor, 2 - Poor, 3 - Fair, 4 - Good, 5 - Excellent)</div>                 
          <h4 id={"onboardingExperienceScore"}>Overall, how would you rate your onboarding and training experience with Mueller?</h4>   
          <StarRatings 
            rating={surveyResponse.onboardingExperienceScore ?? 0}
            changeRating={(nextRating) => updateSurveyResponse("onboardingExperienceScore", nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst} 
          /> 
          <h4 id={"didYouCompleteViitaCourse"}>
            Did you complete the VIITA course as part of the onboarding process?
          </h4>  
          <>
            <MField 
                key="didYouCompleteViitaCourse"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}, {label: "N/A (I had prior inspection experience)", value: null}] }}
                onChange = {(e, i) => updateSurveyResponse("didYouCompleteViitaCourse", i?.value)}
              />            
            {{...surveyResponse}.didYouCompleteViitaCourse === true ? 
            <>
              <h6 id="wasViitaCourseHelpful">Was the VIITA course helpful in preparing you for the role?</h6> 
              <MField 
                  key="wasViitaCourseHelpful"
                  dataType={DATA_TYPE_DROPDOWN2}
                  dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}, {label: "Somewhat", value: false}] }}
                  onChange = {(e, i) => updateSurveyResponse("wasViitaCourseHelpful", i?.value)}
                />            
              {{...surveyResponse}.wasViitaCourseHelpful != true ? 
              <>
                <h6 id="whatCouldBeImprovedAboutViitaCourse">What could be improved about the VIITA course?</h6> 
                <MField 
                    key="whatCouldBeImprovedAboutViitaCourse"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whatCouldBeImprovedAboutViitaCourse", i)}
                  />
              </> : <div/>}              
            </> : <div/>}
          </>                     
          <h4 id={"recruiterOnboardingScore"}>How would you rate the recruiter (Hiring Coordinator) who supported your onboarding process?</h4>  
          <StarRatings             
            rating={surveyResponse.recruiterOnboardingScore ?? 0}
            changeRating={(nextRating) => updateSurveyResponse("recruiterOnboardingScore", nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}
          />    
          <h4 id={"wasRecruiterClearDuringScreeningCall"}>Did the recruiter clearly explain the role and expectations during the initial screening call?</h4>  
          <>
            <MField                 
                key="wasRecruiterClearDuringScreeningCall"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}, {label: "N/A", value: null}] }}
                onChange = {(e, i) => updateSurveyResponse("wasRecruiterClearDuringScreeningCall", i?.value)}
              />            
            {{...surveyResponse}.wasRecruiterClearDuringScreeningCall === false ? 
            <>
              <h6 id="whatWasUnclearDuringScreeningCall">What was unclear or missing?</h6> 
              <MField                     
                    key="whatWasUnclearDuringScreeningCall"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whatWasUnclearDuringScreeningCall", i)}
              />                              
            </> : <div/>}
          </>  
          <h4 id={"didRecruiterProvideHelpfulSupportDuringScreeningCall"}>Did the recruiter provide timely, helpful support throughout the onboarding process?</h4>  
          <>
            <MField                 
                key="didRecruiterProvideHelpfulSupportDuringScreeningCall"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}, {label: "N/A", value: null}] }}
                onChange = {(e, i) => updateSurveyResponse("didRecruiterProvideHelpfulSupportDuringScreeningCall", i?.value)}
              />            
            {{...surveyResponse}.didRecruiterProvideHelpfulSupportDuringScreeningCall === false ? 
            <>
              <h6 id={"whereDidSupportBreakDownDuringScreeningCall"}>Where did the support break down?</h6> 
              <MField                     
                    key="whereDidSupportBreakDownDuringScreeningCall"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whereDidSupportBreakDownDuringScreeningCall", i)}
              />                              
            </> : <div/>}
          </>  
          <h4 id={"didRecuiterResolveTechnicalIssuesPromptly"}>Did the recruiter help resolve any technical or login issues promptly?</h4>  
          <>
            <MField                 
                key="didRecuiterResolveTechnicalIssuesPromptly"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}, {label: "N/A (I had no issues)", value: null}] }}
                onChange = {(e, i) => updateSurveyResponse("didRecuiterResolveTechnicalIssuesPromptly", i?.value)}
              />            
            {{...surveyResponse}.didRecuiterResolveTechnicalIssuesPromptly === false ? 
            <>
              <h6 id={"whatSupportWasNotProvided"}>What support did you need that wasn’t provided?</h6> 
              <MField                     
                    key="whatSupportWasNotProvided"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whatSupportWasNotProvided", i)}
              />                              
            </> : <div/>}
          </> 
          <h4 id={"didOnboardingPaperworkMakeFormCompletionEasy"}>Did the onboarding paperwork and email instructions make it easy to complete your forms and documents?</h4>  
          <>
            <MField                 
                key="didOnboardingPaperworkMakeFormCompletionEasy"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}] }}
                onChange = {(e, i) => updateSurveyResponse("didOnboardingPaperworkMakeFormCompletionEasy", i?.value)}
              />            
            {{...surveyResponse}.didOnboardingPaperworkMakeFormCompletionEasy === false ? 
            <>
              <h6 id={"whatWasConfusingWithOnboardingPaperwork"}>What was confusing or difficult with the paperwork?</h6> 
              <MField                     
                    key="whatWasConfusingWithOnboardingPaperwork"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whatWasConfusingWithOnboardingPaperwork", i)}
              />                              
            </> : <div/>}
          </>   
          <h4 id={"wereYouAbleToLogIntoSystemsWithoutIssue"}>Were you able to log into all required systems without major issues?</h4>  
          <>
            <MField                 
                key="wereYouAbleToLogIntoSystemsWithoutIssue"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}] }}
                onChange = {(e, i) => updateSurveyResponse("wereYouAbleToLogIntoSystemsWithoutIssue", i?.value)}
              />            
            {{...surveyResponse}.wereYouAbleToLogIntoSystemsWithoutIssue === false ? 
            <>
              <h6 id={"whichSystemsCausedTrouble"}>Which system(s) did you have trouble accessing?</h6> 
              <MField                     
                    key="whichSystemsCausedTrouble"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whichSystemsCausedTrouble", i)}
              />                              
            </> : <div/>}
          </>      
          <h4 id={"managerOnboardingSupportScore"}>How would you rate the support provided by your manager during the hiring and onboarding process?</h4>  
          <StarRatings             
            rating={surveyResponse.managerOnboardingSupportScore ?? 0}
            changeRating={(nextRating) => updateSurveyResponse("managerOnboardingSupportScore", nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}
          />                                                          
          <h4 id={"didYouMeetWithManagerWithinTwoDaysOfHire"}>Did you meet with your manager within the first 2 days of being hired?</h4>  
          <>
            <MField                 
                key="didYouMeetWithManagerWithinTwoDaysOfHire"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}] }}
                onChange = {(e, i) => updateSurveyResponse("didYouMeetWithManagerWithinTwoDaysOfHire", i?.value)}
              />            
            {{...surveyResponse}.didYouMeetWithManagerWithinTwoDaysOfHire === false ? 
            <>
              <h6 id={"whenDidYouFirstMeetWithManager"}>When did you first connect with your manager?</h6> 
              <MField                     
                    key="whenDidYouFirstMeetWithManager"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whenDidYouFirstMeetWithManager", i)}
              />                              
            </> : <div/>}
          </>   
          <h4 id={"didOrientationClarifyExpectationsForFirstDay"}>Did that initial orientation meeting help clarify expectations and action items for your first day in the field?</h4>  
          <>
            <MField 
                key="didOrientationClarifyExpectationsForFirstDay"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}] }}
                onChange = {(e, i) => updateSurveyResponse("didOrientationClarifyExpectationsForFirstDay", i?.value)}
              />            
            {{...surveyResponse}.didOrientationClarifyExpectationsForFirstDay === false ? 
            <>
              <h6 id={"whatCouldHaveImprovedOrientation"}>What could have made that meeting more helpful?</h6> 
              <MField                   
                    key="whatCouldHaveImprovedOrientation"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whatCouldHaveImprovedOrientation", i)}
              />                              
            </> : <div/>}
          </> 
          <h4 id={"didTrainingMaterialsHelpPrepareForFirstInspection"}>Did the training materials and videos provided by your recruiter and manager help prepare you for your first inspection?</h4>  
          <>
            <MField                 
                key="didTrainingMaterialsHelpPrepareForFirstInspection"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}, {label: "I did not review them", value: null}] }}
                onChange = {(e, i) => updateSurveyResponse("didTrainingMaterialsHelpPrepareForFirstInspection", i?.value)}
              />            
            {{...surveyResponse}.didTrainingMaterialsHelpPrepareForFirstInspection === false ? 
            <>
              <h6 id={"whatCouldHaveBeenImprovedWithTrainingMaterials"}>What would have made the materials more helpful?</h6> 
              <MField                     
                    key="whatCouldHaveBeenImprovedWithTrainingMaterials"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whatCouldHaveBeenImprovedWithTrainingMaterials", i)}
              />                              
            </> : <div/>}
          </>
          <h4 id={"wereYouProvidedNumberForFieldSupportHotline"}>Were you provided the phone number for the Field Support Hotline during the onboarding process to contact for inspection-related questions and concerns?</h4>  
          <MField                 
                key="wereYouProvidedNumberForFieldSupportHotline"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}, {label: "I'm not sure", value: null}] }}
                onChange = {(e, i) => updateSurveyResponse("wereYouProvidedNumberForFieldSupportHotline", i?.value)}
          />    
          <h4 id={"didYouFeelSupportedByYourManagerDuringFirstTwoWeeks"}>Did you feel supported by your manager during your first two weeks on the job?</h4>  
          <>
            <MField                 
                key="didYouFeelSupportedByYourManagerDuringFirstTwoWeeks"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}] }}
                onChange = {(e, i) => updateSurveyResponse("didYouFeelSupportedByYourManagerDuringFirstTwoWeeks", i?.value)}
              />            
            {{...surveyResponse}.didYouFeelSupportedByYourManagerDuringFirstTwoWeeks === false ? 
            <>
              <h6 id={"whatSupportDidYouNotReceive"}>What support did you need but didn’t receive?</h6> 
              <MField                     
                    key="whatSupportDidYouNotReceive"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whatSupportDidYouNotReceive", i)}
              />                              
            </> : <div/>}
          </>  
          <h4 id={"howLongAfterInitialMeetingDidYouCompleteAnInspection"}>How long after your meeting with your manager did you complete your first inspection?</h4>  
          <>
            <MField                 
                key="howLongAfterInitialMeetingDidYouCompleteAnInspection"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [
                  {label: "Less than 1 week", value: 1}, {label: "1-2 weeks", value: 2},
                  {label: "3-4 weeks", value: 3}, {label: "More than 4 weeks", value: 4},
                  {label: "I don't remember", value: 5}
                ] }}
                onChange = {(e, i) => updateSurveyResponse("howLongAfterInitialMeetingDidYouCompleteAnInspection", i?.value)}
              />            
            {{...surveyResponse}.howLongAfterInitialMeetingDidYouCompleteAnInspection !== 1 ? 
            <>
              <h6 id={"whatCausedTheDelay"}>What caused the delay?</h6> 
              <MField                     
                    key="whatCausedTheDelay"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whatCausedTheDelay", i)}
              />                              
            </> : <div/>}
          </>                                                    
          <h4 id={"didYouFeelConfidentBeforeCompletingYourFirstInspection"}>Did you feel confident before completing your first inspection?</h4>  
          <>
            <MField                 
                key="didYouFeelConfidentBeforeCompletingYourFirstInspection"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}] }}
                onChange = {(e, i) => updateSurveyResponse("didYouFeelConfidentBeforeCompletingYourFirstInspection", i?.value)}
              />            
            {{...surveyResponse}.didYouFeelConfidentBeforeCompletingYourFirstInspection === false ? 
            <>
              <h6 id="whatMadeYouFeelUnprepared">What made you feel unprepared?</h6> 
              <MField                     
                    key="whatMadeYouFeelUnprepared"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whatMadeYouFeelUnprepared", i)}
              />                              
            </> : <div/>}
          </>  
          <h4 id="didYouReceiveEnoughInspectionsInFirstTwoWeeks">Did you receive enough inspections in your first two weeks?</h4>  
          <>
            <MField                 
                key="didYouReceiveEnoughInspectionsInFirstTwoWeeks"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}, {label: "N/A", value: null}] }}
                onChange = {(e, i) => updateSurveyResponse("didYouReceiveEnoughInspectionsInFirstTwoWeeks", i?.value)}
              />            
            {{...surveyResponse}.didYouReceiveEnoughInspectionsInFirstTwoWeeks === false ? 
            <>
              <h6 id="didYouLetYourManagerKnowYouNeededMoreWork">Did you let your manager know you needed more work?</h6> 
              <MField                     
                    key="didYouLetYourManagerKnowYouNeededMoreWork"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("didYouLetYourManagerKnowYouNeededMoreWork", i)}
              />                              
            </> : <div/>}
          </>      
          <h4 id="doYouUnderstandYourJobResponsibilitiesToday">Do you understand your job responsibilities today?</h4>  
          <>
            <MField                 
                key="doYouUnderstandYourJobResponsibilitiesToday"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}] }}
                onChange = {(e, i) => updateSurveyResponse("doYouUnderstandYourJobResponsibilitiesToday", i?.value)}
              />            
            {{...surveyResponse}.doYouUnderstandYourJobResponsibilitiesToday === false ? 
            <>
              <h6 id="whatResponsibilitiesCouldBeClarified">What would you like clarification on?</h6> 
              <MField                     
                    key="whatResponsibilitiesCouldBeClarified"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whatResponsibilitiesCouldBeClarified", i)}
              />                              
            </> : <div/>}
          </>   
          <h4 id="doYouUnderstandHowYourPerformanceIsMeasured">Do you understand how your performance is measured?</h4>  
          <>
            <MField                 
                key="doYouUnderstandHowYourPerformanceIsMeasured"
                dataType={DATA_TYPE_DROPDOWN2}
                dataTypeOptions= {{ options: [{label: "Yes", value: true}, {label: "No", value: false}] }}
                onChange = {(e, i) => updateSurveyResponse("doYouUnderstandHowYourPerformanceIsMeasured", i?.value)}
              />            
            {{...surveyResponse}.doYouUnderstandHowYourPerformanceIsMeasured === false ? 
            <>
              <h6 id="whatIsUnclearAboutPerformanceExpectations">What’s unclear about performance expectations?</h6> 
              <MField                     
                    key="whatIsUnclearAboutPerformanceExpectations"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange= {(e, i) => updateSurveyResponse("whatResponsibilitiesCouldBeClarified", i)}
              />                              
            </> : <div/>}
          </>                          
          <h4 id="additionaFeedbackForOnboardingExperience">Do you have any other feedback about your onboarding experience that wasn’t covered above?</h4>                              
          <TextField            
            label=""
            maxLength={COMMENTS_MAXLENGTH}
            rows={2}
            onChange={(val) => updateSurveyResponse("additionaFeedbackForOnboardingExperience", val)}
          />          
          <h3>Thank you for your time!</h3>
          <Button primary raised onClick={submitFeedback}>Submit</Button>
        </>
      }
      {status === STATUS.RATED &&
        <div className="satisfaction-survey-complete">
          <h3>Thank you for your feedback!</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button primary raised>Contact Us</Button>
          </a>
        </div>
      }
      {status === STATUS.EXPIRED &&
        <div className="satisfaction-survey-complete">
          <h3>This link has expired.  Please contact customer service with any additional feedback.</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button primary raised>Contact Us</Button>
          </a>
        </div>
      }
      {status === STATUS.ERROR &&
        <div className="satisfaction-survey-complete">
          <h3>Oops!  You appear to have reached this page in error.</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button primary raised>Contact Us</Button>
          </a>
        </div>
      }
    </div>  
  );
};

const mapDispatchToProps = dispatch => ({
  makeToast: text => {
    responseHandler.customToast(text, "error", dispatch);
  }
});

export default connect(null, mapDispatchToProps)(OnboardingSatisfactionSurvey);